import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import { selectCartCount } from '../../store/slices/cartSlice';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemIcon,
  ListItemText, IconButton, Divider, Chip, Badge
} from '@mui/material';
import {
  Dashboard, ShoppingCart, RequestQuote, Chat, Person,
  Menu as MenuIcon, Logout, Home, ShoppingCartOutlined, Wallet, Build
} from '@mui/icons-material';
import LanguageSwitcher from '../LanguageSwitcher';

const drawerWidth = 260;
const menuItems = [
  { text: 'customer_dashboard', icon: <Dashboard />, path: '/customer' },
  { text: 'my_orders', icon: <ShoppingCart />, path: '/customer/orders' },
  { text: 'my_quotes', icon: <RequestQuote />, path: '/customer/quotes' },
  { text: 'Customization Requests', icon: <Build />, path: '/customer/customization-requests' },
  { text: 'payment_methods', icon: <Wallet />, path: '/customer/payment-methods' },
  { text: 'messages', icon: <Chat />, path: '/customer/chat' },
  { text: 'profile', icon: <Person />, path: '/customer/profile' },
];

const CustomerLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartCount = useSelector(selectCartCount);
  const [mobileOpen, setMobileOpen] = useState(false);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} color="primary">OSESG</Typography>
        <Chip label="My Account" color="secondary" size="small" sx={{ mt: 0.5 }} />
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        <ListItem
          button component={Link} to="/customer/cart"
          selected={location.pathname === '/customer/cart'}
          sx={{
            borderRadius: 2, mb: 0.5,
            '&.Mui-selected': { bgcolor: 'secondary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } },
            '&:hover': { bgcolor: 'secondary.light', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Badge badgeContent={cartCount} color="error"><ShoppingCartOutlined /></Badge>
          </ListItemIcon>
          <ListItemText primary="My Cart" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: cartCount > 0 ? 700 : 400 }} />
        </ListItem>
        {menuItems.map((item) => (
          <ListItem
            button key={item.path} component={Link} to={item.path}
            selected={location.pathname === item.path}
            sx={{
              borderRadius: 2, mb: 0.5,
              '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } },
              '&:hover': { bgcolor: 'primary.light', color: 'white', '& .MuiListItemIcon-root': { color: 'white' } }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText primary={t(item.text)} primaryTypographyProps={{ fontSize: '0.9rem' }} />
          </ListItem>
        ))}
      </List>
      <Divider />
      <List sx={{ px: 1 }}>
        <ListItem button component={Link} to="/products">
          <ListItemIcon sx={{ minWidth: 40 }}><Home /></ListItemIcon>
          <ListItemText primary={t('products')} />
        </ListItem>
        <ListItem button onClick={() => { dispatch(logout()); navigate('/login'); }}>
          <ListItemIcon sx={{ minWidth: 40 }}><Logout /></ListItemIcon>
          <ListItemText primary={t('logout')} />
        </ListItem>
      </List>
    </Box>
  );

  return (
    // Force LTR for the dashboard chrome — see SupplierLayout.js for why.
    <Box sx={{ display: 'flex', direction: 'ltr' }}>
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` }, bgcolor: '#1a237e' }}>
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flex: 1 }}>{t('customer_dashboard')}</Typography>
          <LanguageSwitcher color="inherit" />
          <IconButton color="inherit" onClick={() => navigate('/customer/cart')}>
            <Badge badgeContent={cartCount} color="error">
              <ShoppingCartOutlined />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { width: drawerWidth } }}>
          {drawer}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` }, mt: 8, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default CustomerLayout;
